/* ==========================================================================
   DRIVEOLIO — DD behaviour layer
   Progressive enhancement only. Every feature works without JS:
     - filters/sort are real <form> GET submits (theme's facets.js upgrades them)
     - FAQ uses native <details>
     - quick-add is a real <form> POST to /cart/add, JS only intercepts it
   Nothing here touches theme internals, so it cannot break stock behaviour.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ------------------------------------------------- 1. mobile filter panel */
  function initFilterToggle() {
    $$('[data-dd-filter-toggle]').forEach((btn) => {
      if (btn.dataset.ddBound) return;
      btn.dataset.ddBound = '1';

      const targetSel = btn.getAttribute('data-dd-filter-toggle');
      const panel = targetSel ? $(targetSel) : null;
      if (!panel) return;

      // start collapsed on small screens only
      const sync = () => {
        const small = window.matchMedia('(max-width: 989px)').matches;
        if (!small) {
          panel.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
        } else if (!panel.dataset.ddUserOpened) {
          panel.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        }
      };
      sync();
      on(window, 'resize', sync);

      on(btn, 'click', () => {
        const isHidden = panel.hasAttribute('hidden');
        if (isHidden) {
          panel.removeAttribute('hidden');
          panel.dataset.ddUserOpened = '1';
          btn.setAttribute('aria-expanded', 'true');
        } else {
          panel.setAttribute('hidden', '');
          delete panel.dataset.ddUserOpened;
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ---------------------------------------------------- 2. auto-submit sort */
  function initAutoSubmit() {
    $$('[data-dd-autosubmit]').forEach((el) => {
      if (el.dataset.ddBound) return;
      el.dataset.ddBound = '1';
      on(el, 'change', () => {
        const form = el.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ------------------------------------------------------- 3. oil / spec finder
     This used to build ?filter.p.vendor=/-tag= URLs. Those params are inert on
     this store because Search & Discovery is not installed, so the finder
     always returned the unfiltered catalog while looking like it worked.

     Replaced with the two mechanisms that were measured to actually narrow.
     Totals below are real: collection.products_count and search.results_count
     read off the live pages, not the 24-per-page slice.

       /collections/all                     -> 523  (baseline)
       /collections/all/castrol             -> 40   (tag count is exactly 40)
       /collections/all/auto_oil            -> 347  (tag count is exactly 347)
       /collections/all/castrol+selenia     -> 0    (tags AND, as intended)
       /collections/all/auto-oil            -> 523  (wrong handle = silent no-op)
       /search?type=product&q=5W-30         -> 79
       /search?type=product&q=Castrol 5W-30 -> 14   (search ANDs its terms)

     A value backed by a real product tag carries data-dd-h, the handle written
     by Liquid, and goes into the tag path. A value that only exists inside
     product titles, a viscosity grade for example, has no tag to point at, so
     it goes to search instead. The wrong-handle line above is why the handle is
     never derived here: one bad character and the shopper silently gets
     everything. */
  function initFinder() {
    $$('[data-dd-finder]').forEach((form) => {
      if (form.dataset.ddBound) return;
      form.dataset.ddBound = '1';

      on(form, 'submit', (e) => {
        e.preventDefault();

        const base = (form.getAttribute('data-dd-finder') || '/collections/all').replace(/\/+$/, '');
        const handles = [];
        const words = [];

        $$('select[data-dd-kind]', form).forEach((field) => {
          const value = (field.value || '').trim();
          if (!value) return;
          words.push(value);
          const chosen = field.options[field.selectedIndex];
          const handle = chosen ? chosen.getAttribute('data-dd-h') : '';
          if (field.getAttribute('data-dd-kind') === 'tag' && handle) handles.push(handle);
        });

        let url = base;
        if (words.length > 0 && handles.length === words.length) {
          url = base + '/' + handles.join('+');
        } else if (words.length > 0) {
          url = '/search?type=product&q=' + encodeURIComponent(words.join(' '));
        }

        window.location.href = url;
      });
    });
  }

  /* ---------------------------------------------------------- 4. quick add */
  function bumpCartCount(delta) {
    // Update any visible cart bubble without assuming a specific theme markup.
    const targets = $$(
      '.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble > span:first-child, [data-cart-count]'
    );
    targets.forEach((node) => {
      const current = parseInt((node.textContent || '').replace(/\D/g, ''), 10);
      if (!Number.isNaN(current)) node.textContent = String(current + delta);
    });
    const bubble = $('.cart-count-bubble');
    if (bubble) bubble.hidden = false;
  }

  function toast(message) {
    let box = $('#dd-toast');
    if (!box) {
      box = document.createElement('div');
      box.id = 'dd-toast';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      Object.assign(box.style, {
        position: 'fixed',
        zIndex: '9999',
        bottom: '2rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#101317',
        color: '#fff',
        font: '600 13px/1 inherit',
        padding: '14px 18px',
        borderRadius: '4px',
        boxShadow: '0 6px 24px rgba(0,0,0,.22)',
        opacity: '0',
        transition: 'opacity .2s ease',
        pointerEvents: 'none',
      });
      document.body.appendChild(box);
    }
    box.textContent = message;
    requestAnimationFrame(() => {
      box.style.opacity = '1';
    });
    clearTimeout(toast._t);
    toast._t = setTimeout(() => {
      box.style.opacity = '0';
    }, 2200);
  }

  function initQuickAdd() {
    // Product-page form included: without this the PDP did a native POST and
    // the browser landed on /cart instead of opening the drawer.
    $$('form[data-dd-quick-add], form[action*="/cart/add"].dd-pdp__form').forEach((form) => {
      if (form.dataset.ddBound) return;
      form.dataset.ddBound = '1';

      on(form, 'submit', async (e) => {
        // If fetch is unavailable, let the browser submit normally.
        if (!window.fetch) return;

        // "Buy now" carries its own formaction (return_to=/checkout). Let that
        // one submit natively so the shopper really lands on checkout.
        const via = e.submitter;
        if (via && via.hasAttribute('formaction')) return;

        e.preventDefault();

        const btn = via || form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;

        const drawer = $('cart-drawer');
        const canRender = drawer && typeof drawer.renderContents === 'function';

        try {
          const fd = new FormData(form);
          // Ask Shopify to return the rendered drawer + bubble alongside the add,
          // which is exactly the payload cart-drawer.renderContents() expects.
          if (canRender) {
            fd.append('sections', 'cart-drawer,cart-icon-bubble');
            fd.append('sections_url', window.location.pathname);
          }

          const res = await fetch(canRender ? '/cart/add' : '/cart/add.js', {
            method: 'POST',
            headers: { Accept: canRender ? 'application/javascript' : 'application/json' },
            body: fd,
          });
          if (!res.ok) throw new Error('add failed');
          const data = await res.json();

          flyToCart(form);

          if (canRender && data.sections) {
            // Hand off to the theme so its own drawer stays the source of truth
            if (typeof drawer.open === 'function') drawer.open();
            drawer.renderContents(data);

            // The drawer renders BLANK on the first add without this line.
            // base.css collapses the drawer when the host carries .is-empty:
            //   cart-drawer.is-empty .drawer__header,
            //   cart-drawer.is-empty .cart-drawer__body,
            //   cart-drawer.is-empty .drawer__footer { height:0; overflow:hidden }
            // The class sits on <cart-drawer> itself, but renderContents only
            // swaps #CartDrawer's innerHTML and tests .drawer__inner for the
            // class - so it never clears the host and every region stays at
            // height:0 until a full page reload re-renders it server-side.
            // We just added a line item, so the cart cannot be empty.
            drawer.classList.remove('is-empty');
          } else {
            const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
            bumpCartCount(qty);
            toast(form.getAttribute('data-dd-added-label') || 'Added to cart');
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
        } catch (err) {
          // Hard fallback: real form submit so the customer is never stuck.
          form.submit();
          return;
        } finally {
          if (btn) btn.disabled = false;
        }
      });
    });
  }

  /* Flies a copy of the product image into the cart icon. Purely decorative:
     wrapped so a missing image or reduced-motion preference simply skips it. */
  function flyToCart(form) {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const scope = form.closest('.dd-card, .dd-pdp, .dd-spot, .dd-show__lead') || document;
      const src = scope.querySelector('img');
      const target = $('#cart-icon-bubble');
      if (!src || !target) return;

      const a = src.getBoundingClientRect();
      const b = target.getBoundingClientRect();
      if (!a.width || !b.width) return;

      const ghost = src.cloneNode(true);
      ghost.removeAttribute('loading');
      ghost.className = 'dd-fly';
      ghost.style.left = a.left + 'px';
      ghost.style.top = a.top + 'px';
      ghost.style.width = a.width + 'px';
      ghost.style.height = a.height + 'px';
      document.body.appendChild(ghost);

      const dx = b.left + b.width / 2 - (a.left + a.width / 2);
      const dy = b.top + b.height / 2 - (a.top + a.height / 2);

      requestAnimationFrame(() => {
        ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.12)`;
        ghost.style.opacity = '0.15';
      });
      setTimeout(() => ghost.remove(), 750);
    } catch (err) {
      /* decoration only */
    }
  }

  /* ------------------------------------------------- 5. FAQ single-open mode */
  function initFaq() {
    $$('[data-dd-faq-single]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';
      const items = $$('details', wrap);
      items.forEach((item) => {
        on(item, 'toggle', () => {
          if (!item.open) return;
          items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ------------------------------------------------- 6. header: mobile drawer
     The drawer markup is rendered inline and only hidden with [hidden], so
     with JS disabled the navigation is still present in the DOM and reachable. */
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('dd-drawer-open');
    const opener = drawer.__ddOpener;
    if (opener) opener.setAttribute('aria-expanded', 'false');
    // wait for the slide-out transition before hiding from the a11y tree
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.setAttribute('hidden', '');
    }, 260);
    if (opener) opener.focus();
  }

  function openDrawer(drawer, opener) {
    if (!drawer) return;
    drawer.__ddOpener = opener || null;
    drawer.removeAttribute('hidden');
    // next frame so the transition actually runs
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      document.body.classList.add('dd-drawer-open');
    });
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const closeBtn = drawer.querySelector('[data-dd-drawer-close]');
    if (closeBtn && closeBtn.focus) closeBtn.focus();
  }

  function initDrawer() {
    $$('[data-dd-drawer-open]').forEach((btn) => {
      if (btn.dataset.ddBound) return;
      btn.dataset.ddBound = '1';
      const drawer = $(btn.getAttribute('data-dd-drawer-open'));
      if (!drawer) return;

      on(btn, 'click', () => openDrawer(drawer, btn));

      $$('[data-dd-drawer-close]', drawer).forEach((el) =>
        on(el, 'click', () => closeDrawer(drawer))
      );

      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(drawer);
      });
    });
  }

  /* ------------------------------------------- 7. header: search dropdown
     The panel is a positioned overlay, so opening it no longer reflows the page.
     State is a single class on .dd-hdr, which drives both the panel and the
     blurred scrim in CSS. Closes on the X, on the scrim, on any click outside
     the panel, and on Escape. */
  function initSearchToggle() {
    $$('[data-dd-search-toggle]').forEach((btn) => {
      if (btn.dataset.ddBound) return;
      btn.dataset.ddBound = '1';

      const row = $(btn.getAttribute('data-dd-search-toggle'));
      const hdr = btn.closest('.dd-hdr');
      if (!row || !hdr) return;

      const input = row.querySelector('input[type="search"]');

      const close = () => {
        if (!hdr.classList.contains('is-search-open')) return;
        hdr.classList.remove('is-search-open');
        btn.setAttribute('aria-expanded', 'false');
      };

      const open = () => {
        hdr.classList.add('is-search-open');
        btn.setAttribute('aria-expanded', 'true');
        // wait for visibility to flip or focus() is a no-op
        requestAnimationFrame(() => {
          if (input) input.focus();
        });
      };

      on(btn, 'click', (e) => {
        e.stopPropagation();
        if (hdr.classList.contains('is-search-open')) close();
        else open();
      });

      $$('[data-dd-search-close]', hdr).forEach((el) => on(el, 'click', close));

      // Outside click. The panel and the toggle are the only safe zones.
      on(document, 'click', (e) => {
        if (!hdr.classList.contains('is-search-open')) return;
        if (row.contains(e.target) || btn.contains(e.target)) return;
        close();
      });

      on(document, 'keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ------------------------------------------------------- 8. collection tabs
     All panels are rendered server-side; JS only toggles [hidden]. With JS
     off the first panel stays visible and every product is still in the DOM. */
  function initTabs() {
    $$('[data-dd-tabs]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';

      const tabs = $$('[data-dd-tab]', wrap);
      const panels = $$('[data-dd-tab-panel]', wrap);
      if (!tabs.length || !panels.length) return;

      const select = (idx) => {
        tabs.forEach((t, i) => t.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
        panels.forEach((p, i) => {
          if (i === idx) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      };

      tabs.forEach((tab, i) => {
        on(tab, 'click', () => select(i));
        on(tab, 'keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const next = e.key === 'ArrowRight'
            ? (i + 1) % tabs.length
            : (i - 1 + tabs.length) % tabs.length;
          tabs[next].focus();
          select(next);
        });
      });
    });
  }

  /* ------------------------------------------------------------ 9. countdown
     The server renders a static placeholder, so if JS never runs the strip
     still reads as a normal offer band. When the deadline passes the clock
     removes itself rather than showing negative time. */
  let cdTimer = null;

  function pad(n) {
    return n < 10 ? `0${n}` : String(n);
  }

  function tickCountdowns() {
    const clocks = $$('[data-dd-countdown]');

    if (!clocks.length) {
      if (cdTimer) {
        clearInterval(cdTimer);
        cdTimer = null;
      }
      return;
    }

    const now = Date.now();

    clocks.forEach((clock) => {
      const raw = clock.getAttribute('data-dd-countdown');
      const end = Date.parse(raw);

      // Unparseable or past deadline: drop the clock, keep the offer copy.
      if (Number.isNaN(end) || end - now <= 0) {
        clock.remove();
        return;
      }

      const left = Math.floor((end - now) / 1000);
      const parts = {
        days: Math.floor(left / 86400),
        hours: Math.floor((left % 86400) / 3600),
        minutes: Math.floor((left % 3600) / 60),
        seconds: left % 60,
      };

      Object.keys(parts).forEach((key) => {
        const node = clock.querySelector(`[data-dd-cd="${key}"]`);
        if (node) node.textContent = key === 'days' ? String(parts[key]) : pad(parts[key]);
      });

      clock.removeAttribute('hidden');
    });
  }

  function initCountdown() {
    if (!$('[data-dd-countdown]')) return;
    tickCountdowns();
    if (!cdTimer) cdTimer = setInterval(tickCountdowns, 1000);
  }

  /* ------------------------------------------------ 10. predictive search
     Uses Shopify's own suggest endpoint with section rendering, so the markup
     comes back as HTML from sections/dd-predictive-search.liquid rather than
     being templated here. The input stays inside a real GET form, so with JS
     off (or if a request fails) submitting still lands on /search.

     Requests are debounced and the in-flight one is aborted when the shopper
     keeps typing, so slow responses can never overwrite newer results. */
  function initPredictiveSearch() {
    $$('[data-dd-psearch]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';

      const input = wrap.querySelector('input[type="search"]');
      const panel = wrap.querySelector('[data-dd-psearch-panel]');
      if (!input || !panel) return;

      const minChars = parseInt(wrap.getAttribute('data-dd-psearch-min') || '2', 10);
      let timer = null;
      let controller = null;
      let lastTerm = '';

      const close = () => {
        panel.hidden = true;
        panel.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        wrap.classList.remove('is-open');
      };

      const open = (html) => {
        panel.innerHTML = html;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        wrap.classList.add('is-open');
      };

      const run = async (term) => {
        if (controller) controller.abort();
        controller = new AbortController();

        const url =
          `${window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/'}`.replace(/\/$/, '') +
          '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,collection,query,page,article' +
          // 10 is Shopify's max; the panel lays results out in a multi-column
          // grid, so more results fill it rather than making it taller.
          '&resources[limit]=10' +
          // Match name, category, variant and vendor — plus SKU so a part
          // number typed by a workshop resolves too. These are Shopify's
          // default predictive fields with variants.sku added.
          '&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku' +
          '&section_id=dd-predictive-search';

        try {
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error('suggest failed');
          const text = await res.text();

          // Shopify wraps section renders in a container div; take its contents.
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const inner = doc.querySelector('.shopify-section') || doc.body;
          const html = (inner.innerHTML || '').trim();

          // Guard against a stale response arriving after a newer keystroke.
          if (term !== lastTerm) return;
          if (html) open(html);
          else close();
        } catch (err) {
          if (err.name !== 'AbortError') close();
        }
      };

      on(input, 'input', () => {
        const term = input.value.trim();
        lastTerm = term;
        clearTimeout(timer);
        if (term.length < minChars) {
          close();
          return;
        }
        timer = setTimeout(() => run(term), 180);
      });

      on(input, 'focus', () => {
        const term = input.value.trim();
        if (term.length >= minChars && panel.innerHTML.trim()) {
          panel.hidden = false;
          wrap.classList.add('is-open');
        }
      });

      // Escape closes; Down arrow moves into the results list.
      on(input, 'keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          return;
        }
        if (e.key === 'ArrowDown' && !panel.hidden) {
          const first = panel.querySelector('a');
          if (first) {
            e.preventDefault();
            first.focus();
          }
        }
      });

      on(panel, 'keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          input.focus();
          return;
        }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const links = $$('a', panel);
        if (!links.length) return;
        const at = links.indexOf(document.activeElement);
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          const next = links[Math.min(at + 1, links.length - 1)];
          if (next) next.focus();
        } else if (at <= 0) {
          input.focus();
        } else {
          links[at - 1].focus();
        }
      });

      // Clicking away closes the panel.
      on(document, 'click', (e) => {
        if (!wrap.contains(e.target)) close();
      });
    });
  }

  /* ---------------------------------------------- 11. DD product gallery
     Thumbnail -> main image switching for snippets/dd-product-gallery.liquid.
     All slides are rendered server-side; this only toggles which one is
     visible, so the gallery works (and is crawlable) with JS disabled. */
  function initProductGallery() {
    $$('[data-dd-pg]').forEach((gal) => {
      if (gal.dataset.ddBound) return;
      gal.dataset.ddBound = '1';

      const thumbs = $$('[data-dd-pg-thumb]', gal);
      const slides = $$('[data-dd-pg-slide]', gal);
      if (!thumbs.length || !slides.length) return;

      const rail = gal.querySelector('[data-dd-pg-rail]');
      const strip = gal.querySelector('.dd-pg__thumbs');

      const show = (idx) => {
        thumbs.forEach((t) => {
          const on = t.getAttribute('data-dd-pg-thumb') === String(idx);
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          // keep the chosen thumb in view inside the scrolling rail
          if (on && strip && t.scrollIntoView) {
            t.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
        slides.forEach((s) => {
          s.classList.toggle('is-active', s.getAttribute('data-dd-pg-slide') === String(idx));
        });
      };

      // Show the bottom fade only while the rail actually overflows. Thumbnails
      // load lazily, so re-check after they arrive and on resize.
      const syncFade = () => {
        if (!rail || !strip) return;
        rail.classList.toggle('is-scroll', strip.scrollHeight - strip.clientHeight > 4);
      };
      syncFade();
      on(window, 'resize', syncFade);
      if (strip) on(strip, 'scroll', syncFade);
      // images changing height as they decode shifts scrollHeight
      $$('img', strip || gal).forEach((im) => {
        if (!im.complete) on(im, 'load', syncFade);
      });
      setTimeout(syncFade, 400);

      thumbs.forEach((t) => {
        on(t, 'click', () => show(t.getAttribute('data-dd-pg-thumb')));
        // Arrow keys move between thumbnails like a real tablist
        on(t, 'keydown', (e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const at = thumbs.indexOf(t);
          const fwd = e.key === 'ArrowDown' || e.key === 'ArrowRight';
          const next = thumbs[fwd ? Math.min(at + 1, thumbs.length - 1) : Math.max(at - 1, 0)];
          if (next) {
            next.focus();
            show(next.getAttribute('data-dd-pg-thumb'));
          }
        });
      });

      // When a variant change swaps the featured image, follow it.
      on(document, 'variant:change', (e) => {
        const img = e && e.detail && e.detail.variant && e.detail.variant.featured_media;
        if (img && typeof img.position === 'number') show(img.position - 1);
      });
    });
  }

  /* -------------------------------------------- 12. quantity steppers
     The input is a real number field, so it already works without JS; these
     buttons are a convenience on top. */
  function initQtySteppers() {
    $$('[data-dd-qty]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';

      const input = wrap.querySelector('input[type="number"]');
      if (!input) return;

      const step = (delta) => {
        const min = parseInt(input.getAttribute('min') || '1', 10);
        const current = parseInt(input.value, 10);
        const base = Number.isNaN(current) ? min : current;
        input.value = Math.max(min, base + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      on(wrap.querySelector('[data-dd-qty-down]'), 'click', () => step(-1));
      on(wrap.querySelector('[data-dd-qty-up]'), 'click', () => step(1));
    });
  }

  /* ------------------------------------------- 13. variant select readout
     The <select name="id"> already submits the right variant on its own. This
     only keeps the price / stock / add-to-cart label in sync with the choice,
     reading values straight off the option element. */
  function initVariantSelect() {
    $$('[data-dd-pdp-variants]').forEach((sel) => {
      if (sel.dataset.ddBound) return;
      sel.dataset.ddBound = '1';

      const root = sel.closest('.dd-pdp__buy') || document;
      const priceBox = $('[data-dd-pdp-price]', root);
      const stockLine = $('[data-dd-pdp-stock]', root);
      const atc = $('[data-dd-pdp-atc]', root);

      const sync = () => {
        const opt = sel.options[sel.selectedIndex];
        if (!opt) return;

        const price = opt.getAttribute('data-price') || '';
        const compare = opt.getAttribute('data-compare') || '';
        const available = opt.getAttribute('data-available') === 'true';

        if (priceBox && price) {
          const now = priceBox.querySelector('.dd-pdp__price-now');
          if (now) now.textContent = price;
          const was = priceBox.querySelector('.dd-pdp__price-was');
          // Only show the strikethrough when there is a real higher compare-at
          if (was) was.style.display = compare && compare !== price ? '' : 'none';
        }

        if (stockLine) {
          stockLine.classList.toggle('dd-pdp__stock--out', !available);
          const label = available ? 'In stock' : 'Out of stock';
          const dot = stockLine.querySelector('.dd-pdp__dot');
          stockLine.textContent = '';
          if (dot) stockLine.appendChild(dot);
          stockLine.appendChild(document.createTextNode(' ' + label));
        }

        if (atc) {
          atc.disabled = !available;
          atc.textContent = available ? 'Add to cart' : 'Out of stock';
        }
      };

      on(sel, 'change', sync);
    });
  }

  /* ------------------------------------------ 14. collapsible long copy
     Collapses the product description to a fixed height with a fade mask.
     max-height is animated to the measured scrollHeight so the transition is
     smooth instead of snapping, and the control hides itself when the text is
     already short enough to need no truncation. */
  function initCollapse() {
    $$('[data-dd-collapse]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';

      const body = wrap.querySelector('[data-dd-collapse-body]');
      const btn = wrap.querySelector('[data-dd-collapse-toggle]');
      const label = wrap.querySelector('[data-dd-collapse-label]');
      if (!body || !btn) return;

      // A grid asking for N rows gets N measured rows. The brand grid is the
      // case that needs it: those vendor names wrap to two or three lines and a
      // CSS grid row is as tall as its tallest cell, so any fixed px guess in
      // Liquid clipped inside the first row and the section looked empty.
      const rowTarget = parseInt(body.dataset.ddCollapseRows || '', 10);
      if (rowTarget > 0) {
        const grid = body.firstElementChild;
        const cell = grid ? grid.firstElementChild : null;
        if (cell && cell.offsetHeight > 0) {
          const gap = parseFloat(getComputedStyle(grid).rowGap) || 0;
          /* A hint that the list continues - not half a visible row.
             This used to be a flat 34% of the cell, which is fine for a 50px
             brand chip (~17px peek) but leaked ~136px of the next row on a
             ~400px category tile: enough of the image showed that it read as a
             broken grid rather than a deliberate cut.

             The cap is inert for the brand grid, whose chips are well under
             80px, and guards any future grid with tall cells. */
          const peek = Math.min(Math.round(cell.offsetHeight * 0.34), 28);
          const px = Math.round(cell.offsetHeight * rowTarget + gap * rowTarget + peek);
          // A custom property, not inline max-height: an inline declaration
          // would outrank the .is-open rule and the panel could never expand.
          body.style.setProperty('--dd-collapse-h', px + 'px');
        }
      }

      const collapsedPx = parseInt(getComputedStyle(body).maxHeight, 10) || 180;

      // Nothing meaningful hidden: drop the affordance and the fade.
      if (body.scrollHeight <= collapsedPx + 8) {
        btn.hidden = true;
        body.style.maxHeight = 'none';
        body.classList.add('dd-pdp__desc--plain');
        return;
      }

      const setFullHeight = () => {
        body.style.setProperty('--dd-desc-full', body.scrollHeight + 'px');
      };
      setFullHeight();
      on(window, 'resize', setFullHeight);

      on(btn, 'click', () => {
        const open = body.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) {
          const next = open ? btn.getAttribute('data-less-label') : btn.getAttribute('data-more-label');
          if (next) label.textContent = next;
        }
        if (open) setFullHeight();
      });
    });
  }

  /* ------------------------------------------------- 15. carousel arrows
     Native horizontal scrolling does the work; these buttons page through it
     and disable themselves at each end. */
  function initCarousels() {
    $$('[data-dd-carousel]').forEach((car) => {
      if (car.dataset.ddBound) return;
      car.dataset.ddBound = '1';

      const section = car.closest('section') || document;
      const prev = $('[data-dd-carousel-prev]', section);
      const next = $('[data-dd-carousel-next]', section);
      if (!prev && !next) return;

      const page = () => Math.max(car.clientWidth * 0.8, 240);

      const sync = () => {
        const max = car.scrollWidth - car.clientWidth - 2;
        if (prev) prev.disabled = car.scrollLeft <= 2;
        if (next) next.disabled = car.scrollLeft >= max;
      };

      on(prev, 'click', () => car.scrollBy({ left: -page(), behavior: 'smooth' }));
      on(next, 'click', () => car.scrollBy({ left: page(), behavior: 'smooth' }));
      on(car, 'scroll', sync, { passive: true });
      on(window, 'resize', sync);
      sync();
    });
  }

  /* --------------------------------------- 16. Shopify recommendations swap
     Upgrades the server-rendered affinity fallback with Shopify's own
     recommendation model. Tries `complementary` first (the merchant-curated
     "goes with this" set from Search & Discovery), then `related`. If neither
     returns anything, the Liquid fallback already on the page is left alone —
     so this can only ever improve the row, never empty it. */
  function initRecommendations() {
    $$('[data-dd-reco]').forEach(async (sec) => {
      if (sec.dataset.ddBound) return;
      sec.dataset.ddBound = '1';

      const id = sec.getAttribute('data-dd-reco-id');
      const limit = sec.getAttribute('data-dd-reco-limit') || '12';
      const track = sec.querySelector('[data-dd-carousel-track]');
      if (!id || !track) return;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base = root.replace(/\/$/, '');

      const fetchIntent = async (intent) => {
        const url =
          `${base}/recommendations/products?section_id=dd-related` +
          `&product_id=${encodeURIComponent(id)}&limit=${encodeURIComponent(limit)}&intent=${intent}`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector('[data-dd-carousel-track]');
        if (!list) return null;
        // Only accept a non-empty result
        return list.children.length ? list.innerHTML : null;
      };

      try {
        const html = (await fetchIntent('complementary')) || (await fetchIntent('related'));
        if (html) track.innerHTML = html;
      } catch (e) {
        /* keep the server-rendered fallback */
      }

      // Newly injected cards need their quick-add forms wired up.
      initQuickAdd();
      initCarousels();
    });
  }

  /* ------------------------------------------------- 18. remote brand logos
     Brand logos can be pulled from an external source (Google favicons,
     Logo.dev, Brandfetch). Those miss often — a marketplace seller name has no
     domain at all — so a failed image is removed and the brand initials that sit
     underneath become visible. No layout shift, no broken-image icon. */
  function initBrandLogos() {
    $$('[data-dd-logo-fallback]').forEach((img) => {
      if (img.dataset.ddBound) return;
      img.dataset.ddBound = '1';

      const fail = () => {
        const wrap = img.closest('.dd-brand__logo');
        if (wrap) wrap.classList.add('is-missing');
      };

      // Google's favicon endpoint answers 404 with a generic globe rather than a
      // network error, so also treat a suspiciously tiny image as a miss.
      // Success has to be reported too, not just failure. The initials sit behind
      // the logo and can no longer be hidden by simply covering them: a
      // transparent PNG scaled with object-fit: contain leaves gaps, and the
      // letter shows through. CSS hides it on .is-ok instead.
      const ok = () => {
        const wrap = img.closest('.dd-brand__logo');
        if (wrap) wrap.classList.add('is-ok');
      };

      const check = () => {
        if (!img.naturalWidth || img.naturalWidth < 24) fail();
        else ok();
      };

      on(img, 'error', fail);
      if (img.complete) check();
      else on(img, 'load', check);
    });
  }

  /* ------------------------------------------------- 19. price range slider
     Enhancement over the two number inputs the theme's facets snippet renders.
     Those inputs remain the real form fields (so filtering works with JS off);
     the slider just writes into them and dispatches change so the theme's own
     facet listener picks it up. */
  function initPriceRange() {
    $$('[data-dd-range]').forEach((wrap) => {
      if (wrap.dataset.ddBound) return;
      wrap.dataset.ddBound = '1';

      const max = parseInt(wrap.getAttribute('data-dd-range-max') || '0', 10);
      const minHandle = wrap.querySelector('[data-dd-range-min-handle]');
      const maxHandle = wrap.querySelector('[data-dd-range-max-handle]');
      const fill = wrap.querySelector('[data-dd-range-fill]');
      const gte = document.getElementById(wrap.getAttribute('data-dd-range-gte'));
      const lte = document.getElementById(wrap.getAttribute('data-dd-range-lte'));
      if (!minHandle || !maxHandle || !max) return;

      // seed the handles from whatever the URL already filtered on
      if (gte && gte.value) minHandle.value = gte.value;
      if (lte && lte.value) maxHandle.value = lte.value;

      const paint = () => {
        const lo = Math.min(+minHandle.value, +maxHandle.value);
        const hi = Math.max(+minHandle.value, +maxHandle.value);
        if (fill) {
          fill.style.left = (lo / max) * 100 + '%';
          fill.style.right = 100 - (hi / max) * 100 + '%';
        }
      };

      // Write to the real inputs and let the theme's facet form react.
      const commit = () => {
        const lo = Math.min(+minHandle.value, +maxHandle.value);
        const hi = Math.max(+minHandle.value, +maxHandle.value);
        if (gte) gte.value = lo > 0 ? lo : '';
        if (lte) lte.value = hi < max ? hi : '';
        [gte, lte].forEach((el) => {
          if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      };

      on(minHandle, 'input', paint);
      on(maxHandle, 'input', paint);
      on(minHandle, 'change', commit);
      on(maxHandle, 'change', commit);

      // typing in the number inputs moves the handles back
      const pull = () => {
        if (gte) minHandle.value = gte.value || 0;
        if (lte) maxHandle.value = lte.value || max;
        paint();
      };
      if (gte) on(gte, 'change', pull);
      if (lte) on(lte, 'change', pull);

      paint();
    });
  }

  /* ------------------------------------------------- 19. sticky offset probe
     Publishes the height of the sticky header as --dd-sticky-top so sticky
     columns (the product gallery) can clear it. Measured rather than hardcoded
     because the header is only sticky when the merchant enables it, and the
     utility bar above it is optional.

     It measures .dd-hdr__main, not .dd-hdr: only the main bar is sticky now, so
     including the utility bar would push the gallery down by a strip of header
     that has already scrolled away. */
  let stickyProbeBound = false;
  function initStickyOffset() {
    const setTop = () => {
      const hdr = $('.dd-hdr--sticky');
      const bar = hdr ? hdr.querySelector('.dd-hdr__main') || hdr : null;
      const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--dd-sticky-top', h + 'px');
    };
    setTop();
    // init() also runs on shopify:section:load, so bind the listeners once
    if (stickyProbeBound) return;
    stickyProbeBound = true;
    on(window, 'resize', setTop);
    // Fonts landing late can change the header height
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setTop);
  }

  /* ------------------------------------------------ 20. sticky header reveal
     Hide the bar while scrolling down, bring it back the instant the direction
     reverses. Two classes only, both on .dd-hdr, and CSS owns the animation:

       .is-pinned  page has scrolled at all -> lift shadow
       .is-hidden  scrolling down, past the reveal threshold -> tuck away

     Guards worth keeping:
       - nothing hides until past `threshold`, so a short page or a small nudge
         near the top can never make the header flicker
       - a direction change smaller than `slack` is ignored, because trackpads
         and iOS rubber-banding emit tiny alternating deltas
       - never hides while its own search panel is open, or the input the user is
         typing into would slide out of view */
  let revealBound = false;
  function initStickyReveal() {
    const hdr = $('.dd-hdr--sticky');
    if (!hdr) return;
    if (revealBound) return;
    revealBound = true;

    const threshold = 220;
    const slack = 6;
    let last = window.scrollY || 0;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = Math.max(window.scrollY || 0, 0);
      const delta = y - last;

      hdr.classList.toggle('is-pinned', y > 2);

      if (Math.abs(delta) < slack) return;

      if (delta > 0 && y > threshold && !hdr.classList.contains('is-search-open')) {
        hdr.classList.add('is-hidden');
      } else if (delta < 0) {
        hdr.classList.remove('is-hidden');
      }
      last = y;
    };

    on(
      window,
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    // Opening search while the bar is tucked away must bring it back first
    on(document, 'click', (e) => {
      if (e.target.closest && e.target.closest('[data-dd-search-toggle]')) hdr.classList.remove('is-hidden');
    });

    update();
  }

  /* ------------------------------------------------------------------- boot */
  function init() {
    initFilterToggle();
    initAutoSubmit();
    initFinder();
    initQuickAdd();
    initFaq();
    initDrawer();
    initSearchToggle();
    initTabs();
    initCountdown();
    initPredictiveSearch();
    initProductGallery();
    initQtySteppers();
    initVariantSelect();
    initCollapse();
    initCarousels();
    initRecommendations();
    initBrandLogos();
    initPriceRange();
    initStickyOffset();
    initStickyReveal();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Re-init when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', init);
})();
